<script setup lang="ts">
import ModalBase from '@/components/modal/modal-container.vue'
import ModalTitle from '@/components/modal/modal-title.vue'
import ModalContent from '@/components/modal/modal-content.vue'
import ModalFooterCancelAndSave from '@/components/modal/modal-footer-cancel-and-save.vue'
import InputSelect from '@/components/input-select.vue'
import ContentLoader from '@/components/content-loader.vue'
import HeroSplashError from '@/components/hero-splash-error.vue'

import { computed, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { refDebounced } from '@vueuse/core'
import { fetchPaginatedAssets } from '@/features/assets/api/fetchPaginatedAssets'

const emit = defineEmits<{
  close: []
  submit: [{ assetCode: string }]
}>()

const PAGE_SIZE = 30

const search = ref('')
const debouncedSearch = refDebounced(search, 350)
const searchText = computed(() => debouncedSearch.value.trim())

const assetsQuery = useQuery({
  queryKey: computed(() => ['credit-notes', 'choose-asset', 'search', searchText.value]),
  queryFn: () =>
    fetchPaginatedAssets({
      page: 1,
      pageSize: PAGE_SIZE,
      filter: searchText.value || undefined,
      sorting: []
    }),
  placeholderData: prev => prev
})

const assetItems = computed(() => {
  const items = assetsQuery.data.value?.items ?? []
  return items.map(x => ({
    ...x,
    display: `${x.name} (Project: ${x.projectCode} | Asset: ${x.assetCode})`
  }))
})

const selected = ref<string | null>(null)

const selectedItemCache = ref<(typeof assetItems.value)[number] | null>(null)
watch(
  [selected, assetItems],
  () => {
    if (!selected.value) {
      selectedItemCache.value = null
      return
    }

    const match = assetItems.value.find(x => x.assetCode === selected.value)
    if (match) selectedItemCache.value = match
  },
  { immediate: true }
)

const assetItemsWithSelected = computed(() => {
  const base = assetItems.value
  const sel = selectedItemCache.value

  if (!sel) return base
  if (base.some(x => x.assetCode === sel.assetCode)) return base

  return [sel, ...base]
})

const canSave = computed(() => !!selected.value)

const isFetching = computed(() => assetsQuery.isFetching.value)
const isInitialLoading = computed(() => assetsQuery.isFetching.value && !assetsQuery.data.value)
const showBlockingError = computed(() => assetsQuery.isError.value && !assetsQuery.data.value)

const isSearching = computed(
  () => !!searchText.value && assetsQuery.isFetching.value && !!assetsQuery.data.value
)

function onCancel() {
  emit('close')
}

async function onSave() {
  if (!canSave.value || isFetching.value) return
  emit('submit', { assetCode: selected.value! })
}

watch(selected, v => {
  if (v) search.value = ''
})
</script>

<template>
  <ModalBase class="choose-asset-modal">
    <ModalTitle>WHICH ASSET?</ModalTitle>

    <ModalContent :class="$style['choose-asset-content']">
      <HeroSplashError
        v-if="showBlockingError"
        title="Unable to load assets."
        sub-title="Please try again. If the problem persists, please contact support." />

      <div v-else :class="$style['choose-asset-fields']">
        <div :class="$style['choose-asset-field']">
          <label :class="$style['choose-asset-label']" for="choose-asset-select"> Asset </label>

          <div :class="$style['choose-asset-control']">
            <InputSelect
              id="choose-asset-select"
              :class="$style['choose-asset-select']"
              :items="assetItemsWithSelected"
              item-text="display"
              item-value="assetCode"
              searchable
              placeholder="Please select"
              v-model:selected="selected"
              v-model:search="search"
              :disabled="isInitialLoading" />

            <div v-if="isInitialLoading" :class="$style['choose-asset-loader']">
              <ContentLoader
                loading
                variant="table"
                :rows="3"
                :table-header="false"
                :cols="['110px', '100% - 110px']" />
            </div>

            <div v-else-if="isSearching" :class="$style['choose-asset-searching']">
              <ContentLoader
                loading
                variant="table"
                :rows="1"
                :table-header="false"
                :cols="['110px', '100% - 110px']" />
            </div>
          </div>
        </div>
      </div>
    </ModalContent>

    <ModalFooterCancelAndSave
      :class="$style['choose-asset-footer']"
      :pending="isFetching"
      :enabled="!isFetching && canSave"
      @cancel="onCancel"
      @save="onSave" />
  </ModalBase>
</template>

<style module>
.choose-asset-content {
  padding-bottom: 145px;
}

.choose-asset-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.choose-asset-field {
  display: grid;
  grid-template-columns: 110px 1fr;
  column-gap: 16px;
  align-items: center;
}

.choose-asset-label {
  margin: 0;
  white-space: nowrap;
}

.choose-asset-control {
  min-width: 0;
}

.choose-asset-select {
  width: 100%;
  max-width: 100%;
}

.choose-asset-loader {
  margin-top: 10px;
}

.choose-asset-searching {
  margin-top: 6px;
  opacity: 0.85;
}

.choose-asset-footer {
  margin-top: 12px;
}

@media (max-width: 640px) {
  .choose-asset-field {
    grid-template-columns: 1fr;
    row-gap: 6px;
    align-items: stretch;
  }
}

.choose-asset-select :global(.dropdown-menu) {
  overflow-x: hidden;
}

.choose-asset-select :global(.dropdown-item),
.choose-asset-select :global(.dropdown > .btn) {
  white-space: pre-line;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.choose-asset-select :global(.dropdown-item span) {
  display: block;
  line-height: 1.25;
}

.choose-asset-select :global(.dropdown > .btn > span) {
  align-items: flex-start !important;
}
</style>
